import { FC, useEffect, useMemo } from "react";
import { useRecoilValue } from "recoil";

import { useRideRoutes, useTrackingActions } from "@ryde/redux";
import {
  editFormDataAF,
  IRideDto,
  LiveMapDisplayModeEnum,
  loadingEditFormContentAtomFamily,
  MapRouteBlockTypeEnum,
  RideCrudModeKey,
  RideCrudModeKeyTuple,
} from "@ryde/shared";

import { RideWaypointMapControlsHost } from "../../../../layout-edit";
import { RouteBlock } from "../../../../map";
import { RideMapStyled, RideMapWrapperStyled } from "./ride.styled";

export const RideMap: FC = () => {
  const rideDto = useRecoilValue<IRideDto | undefined>(
    editFormDataAF(RideCrudModeKeyTuple),
  );
  const crudDataLoading = useRecoilValue(
    loadingEditFormContentAtomFamily(RideCrudModeKey.uiUnit),
  );
  const { trackingRoutes, rideRoutes } = useRideRoutes(rideDto);
  const { fetchTrackingForEditingRide } = useTrackingActions();

  // Single-ride view always shows the full GPS trail. The live-map poll stores only the last position
  // (lastTrackingOnly) in the shared tracking state, so opening a ride from the map would otherwise show
  // just the vehicle marker with no trail. Fetch the full tracking for the opened ride so full mode has
  // a trail to draw.
  useEffect(() => {
    fetchTrackingForEditingRide(rideDto?.id);
  }, [rideDto?.id, fetchTrackingForEditingRide]);

  const mapHiddenStateCondition = useMemo(() => {
    return Boolean(crudDataLoading);
  }, [crudDataLoading]);

  return (
    <RideMapWrapperStyled className="RideDrawer-Map">
      <RideMapStyled>
        {/*
          Single-ride view invariants — please keep, do NOT revert to the shared live-map behavior:
          - liveMapDisplayMode is pinned to full (stationPins) and there is deliberately NO display-mode
            toggle here. This panel opens on top of the live map; reading/writing the shared display mode
            would re-render every ride under the drawer in full at once (hundreds of markers) and crash
            the browser tab. A single ride is cheap in full, so a toggle is not needed.
          - trackingCarRoutes comes from this view's OWN tracking store (see the fetch effect above),
            not the live-map poll store, so the GPS trail is not trimmed back to a single point.
        */}
        <RouteBlock
          hiddenMapState={mapHiddenStateCondition}
          routes={rideRoutes}
          trackingCarRoutes={trackingRoutes}
          rideDto={rideDto}
          routeBlockType={MapRouteBlockTypeEnum.RideCard}
          liveMapDisplayMode={LiveMapDisplayModeEnum.stationPins}
          mapExtraLayers={<RideWaypointMapControlsHost />}
        />
      </RideMapStyled>
    </RideMapWrapperStyled>
  );
};
